import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { useConfig } from "$global-providers/config-provider/provider";
import { usePatient } from "$global-providers/rxdb-provider/fallback-hooks/patient";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { linkToHome } from "$routes/_lib/links";
import { toTitleCase } from "$routes/patient/_lib/text";
import {
  Box,
  Checkbox,
  makeStyles,
  styled,
  Typography,
} from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

const componentName = "PatientDataSharingConsentModal";

export const DATA_ACCESS_CONSENT_MODAL_TEST_IDS = {
  container: componentName,
  checkBoxButton: `${componentName}.checkBoxButton`,
  continueButton: `${componentName}.continueButton`,
  cancelButton: `${componentName}.cancelButton`,
};

const useStyles = makeStyles((theme) => ({
  warning: {
    borderRadius: "5px",
  },
  warningBreakout: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: "8px",
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: theme.spacing(1, 2),
  },
  warningIcon: {
    color: theme.palette.warning.main,
    height: 50,
    width: 50,
  },
  checkbox: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  checkboxLabel: {
    cursor: "pointer",
    userSelect: "none",
  },
}));

const Dialog = styled(DialogContainer)(({ theme }) => ({
  "&.MuiDialog-root": {
    backdropFilter: "blur(4px)",
  },
  "& .MuiDialog-paper": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
}));

const Body = styled(DialogBody)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const CHECKBOX_ID = "patient-consent-checkbox";

export const DataAccessConsentModal: FC<{ patientId: string }> = ({
  patientId,
}) => {
  const [consented, setConsented] = React.useState(false);
  const classes = useStyles();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const user = useUser();
  const [submitting, setSubmitting] = useState(false);

  const { territory } = useConfig();

  const territoryName = toTitleCase(territory);

  const { data: patient } = usePatient({
    id: patientId,
  });

  const handleUpdatePatientDataSharingConsent = useCallback(
    async (consented: boolean) => {
      setSubmitting(true);
      try {
        await patient.incrementalPatch({
          consent: {
            ...patient.consent,
            territoryUseOfData: consented,
          },
        });
      } catch (e) {
        toast.error(
          formatMessage(
            {
              id: "PatientDataSharingConsentModal.updateConsent.error",
              defaultMessage: "There was a problem: {error}",
            },
            { error: e.message },
          ),
        );
      }
      setSubmitting(false);
    },
    [patient, formatMessage],
  );

  const onClose = () => navigate(linkToHome());

  const open = !!(
    patient &&
    user.organisationId !== patient.organisationId &&
    !patient.consent.territoryUseOfData
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={DATA_ACCESS_CONSENT_MODAL_TEST_IDS.container}
    >
      <DialogHeader
        title={formatMessage({
          id: "patient.consent.dataSharing.title",
          defaultMessage: "Data access consent",
        })}
      />
      <Body>
        <Box className={classes.warningBreakout}>
          <ErrorOutlineIcon className={classes.warningIcon} />
          <Typography className={classes.warningBreakout}>
            <FormattedMessage
              id="patient.consent.dataSharing.warningMessage"
              defaultMessage="Please ensure that the following text is either shown or read to the patient, or their parent or guardian if the patient is under 18 years of age."
            />
          </Typography>
        </Box>
        <Typography variant="body2">
          <FormattedMessage
            values={{ territoryName }}
            id="patient.consent.dataSharing.bodyMessage"
            defaultMessage="We are using an online system called ‘Elephant’ to create electronic medical records. Currently your medical records are controlled by the facility you registered at and can be used in facilities across that state. Do you give your consent for facilities across {territoryName} to be able to access your data?"
          />
        </Typography>

        <label htmlFor={CHECKBOX_ID} className={classes.checkboxLabel}>
          <Box display="flex" alignItems="center">
            <Box mr={2}>
              <Checkbox
                autoFocus
                color="primary"
                id={CHECKBOX_ID}
                value={consented}
                onChange={(e) => {
                  setConsented(e.target.checked);
                }}
                className={classes.checkbox}
                inputProps={{
                  "data-testid":
                    DATA_ACCESS_CONSENT_MODAL_TEST_IDS.checkBoxButton,
                }}
              />
            </Box>
            <Typography variant="body2">
              <FormattedMessage
                id="patient.consent.dataSharing.tickBox"
                defaultMessage="By ticking this box, you confirm that you are happy for us to process your or your child’s data in this way."
              />
            </Typography>
          </Box>
        </label>
      </Body>
      <DialogFooter style={{ display: "flex", gap: 8 }}>
        <Button
          variant="text"
          onClick={() => {
            onClose();
          }}
          data-testid={DATA_ACCESS_CONSENT_MODAL_TEST_IDS.cancelButton}
        >
          <FormattedMessage
            id="patient.consent.denyBtn"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          color="primary"
          onClick={() => {
            handleUpdatePatientDataSharingConsent(consented);
          }}
          disabled={!consented}
          loading={submitting}
          data-testid={DATA_ACCESS_CONSENT_MODAL_TEST_IDS.continueButton}
        >
          <FormattedMessage
            id="patient.consent.confirmationBtn"
            defaultMessage="Accept & Continue"
          />
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
